import {
  useBoundProp,
  useCardSession,
  useCardSessionControls,
  useCardStatus,
  type ComponentFn,
} from '@littlebird/action-cards-view/react';

import { cn } from '@/lib/utils';

import { Avatar } from '../internals/Avatar';
import { lineClampClass } from '../internals/lineClamp';
import {
  ICON_CLASS,
  ICON_SM_CLASS,
  PILL_CLASS,
  sizingClass,
  STRUCK_TITLE_CLASS,
  TEXT_STYLE,
  TONE_CLASS,
} from '../tokens';

import { ICON_GLYPH } from './Icon';
import { Select } from './Select';
import { TextInput } from './TextInput';
import './Change.css';

import type { catalog } from '@littlebird/action-cards-view';

export const Change: ComponentFn<typeof catalog, 'Change'> = ({
  props,
  bindings,
  emit,
  on,
}) => {
  const session = useCardSession();
  const status = useCardStatus();
  const controls = useCardSessionControls();
  const [after] = useBoundProp<string | number | null>(
    props.after,
    bindings?.after
  );
  // At rest the value shows its served label; focus opens the input on the raw value
  const afterPath = bindings?.after;
  const afterFocused = afterPath !== undefined && session.focused === afterPath;
  const editable =
    bindings?.after !== undefined &&
    props.editable !== false &&
    !session.readOnly &&
    (status === 'draft' || status === 'failed');
  const stacked = props.layout === 'stacked';
  const pill = props.appearance === 'pill';
  // The object's footer: plain muted text, as a move's "# Inbox → # Launch"
  const quiet = props.appearance === 'quiet';
  const iconSize = quiet ? ICON_SM_CLASS : ICON_CLASS;
  const titleChange = props.appearance === 'headline';
  const BeforeIcon = props.beforeIcon ? ICON_GLYPH[props.beforeIcon] : null;
  const AfterIcon =
    props.afterIcon || props.icon
      ? ICON_GLYPH[props.afterIcon ?? props.icon ?? 'dot']
      : null;
  const before = props.before ? (
    <s
      title={props.before}
      className={cn(
        'min-w-0 break-words text-foreground-muted',
        titleChange && stacked && STRUCK_TITLE_CLASS,
        stacked && props.lines && lineClampClass(props.lines)
      )}
    >
      {BeforeIcon ? (
        <span
          className="mr-[var(--ac-gap-xs)] inline-flex align-middle"
          style={{ color: props.beforeColor }}
        >
          <BeforeIcon aria-hidden="true" className={cn('inline', iconSize)} />
        </span>
      ) : props.beforeColor ? (
        <span
          className="mr-[var(--ac-gap-xs)] inline-block size-[calc(8*var(--ac-u))] rounded-full"
          style={{ backgroundColor: props.beforeColor }}
        />
      ) : null}
      {props.beforeAvatarName || props.beforeAvatarUrl ? (
        <span className="mr-[var(--ac-gap-xs)] inline-flex align-middle">
          <Avatar
            id={props.before}
            name={props.beforeAvatarName ?? props.before}
            avatarUrl={props.beforeAvatarUrl}
            shape="circle"
            size={16}
          />
        </span>
      ) : null}
      {props.before}
    </s>
  ) : null;

  if (editable && props.options)
    return (
      <Select
        props={{
          value: after ?? null,
          options: props.options,
          previous: props.before,
          previousIcon: props.beforeIcon,
          previousColor: props.beforeColor,
          previousAvatarUrl: props.beforeAvatarUrl,
          label: props.label,
          editable,
          checks: props.checks,
          error: props.error,
          validateOn: props.validateOn,
          appearance: quiet ? 'quiet' : 'pill',
          sizing: props.sizing,
        }}
        bindings={{ value: bindings?.after }}
        emit={emit}
        on={on}
      />
    );

  const afterText = props.afterLabel ?? String(after ?? '');
  return (
    <span
      className={cn(
        'block min-w-0 max-w-full break-words',
        stacked && 'w-full basis-full',
        pill &&
          cn(
            'card-change-pill',
            PILL_CLASS,
            // The pill's height less its two 1px borders, so the one line centres in it
            'inline-block w-fit h-auto overflow-hidden text-ellipsis whitespace-nowrap align-middle leading-[calc(var(--ac-pill-h)-2px)]'
          ),
        quiet &&
          cn(
            'w-fit overflow-hidden text-ellipsis whitespace-nowrap text-foreground-muted',
            TEXT_STYLE.meta
          ),
        !stacked && props.lines && lineClampClass(props.lines),
        titleChange && cn('card-change-headline', TEXT_STYLE.title),
        sizingClass(props.sizing)
      )}
    >
      {before}
      {before && !stacked ? (
        <span aria-hidden="true" className="text-foreground-muted">
          {'\u00a0→\u00a0'}
        </span>
      ) : null}
      {stacked && before && !titleChange ? <br /> : null}
      {editable && props.afterLabel && !afterFocused ? (
        <button
          type="button"
          aria-label={props.label ?? 'Proposed value'}
          className={cn(
            'cursor-text font-semibold',
            props.tone && TONE_CLASS[props.tone]
          )}
          onFocus={() => {
            if (afterPath) controls.focus(afterPath);
          }}
          onClick={() => {
            if (afterPath) controls.focus(afterPath);
          }}
        >
          {props.afterLabel}
        </button>
      ) : editable ? (
        <span className="card-change-after">
          <TextInput
            props={{
              value: String(after ?? ''),
              label: props.label,
              labelHidden: true,
              ariaLabel: props.label ?? 'Proposed value',
              editable,
              checks: props.checks,
              error: props.error,
              validateOn: props.validateOn,
              lines:
                props.lines ??
                (props.appearance === 'headline' ? 2 : undefined),
              textStyle: props.appearance === 'headline' ? 'title' : 'body',
              weightClass: 'font-semibold',
              tone: props.tone,
            }}
            bindings={{ value: bindings?.after }}
            emit={emit}
            on={on}
          />
        </span>
      ) : (
        <strong
          title={afterText}
          className={cn(
            'font-semibold',
            props.tone && TONE_CLASS[props.tone],
            stacked && props.lines && lineClampClass(props.lines)
          )}
        >
          {AfterIcon ? (
            <span
              className="mr-[var(--ac-gap-xs)] inline-flex align-middle"
              style={{ color: props.afterColor }}
            >
              <AfterIcon
                aria-hidden="true"
                className={cn('inline', iconSize)}
              />
            </span>
          ) : props.afterColor ? (
            <span
              className="mr-[var(--ac-gap-xs)] inline-block size-[calc(8*var(--ac-u))] rounded-full"
              style={{ backgroundColor: props.afterColor }}
            />
          ) : null}
          {props.afterAvatarName || props.afterAvatarUrl ? (
            <span className="mr-[var(--ac-gap-xs)] inline-flex align-middle">
              <Avatar
                id={afterText}
                name={props.afterAvatarName ?? afterText}
                avatarUrl={props.afterAvatarUrl}
                shape="circle"
                size={16}
              />
            </span>
          ) : null}
          {afterText}
        </strong>
      )}
    </span>
  );
};
